<template>
  <div
    v-if="isAppLoading"
    role="alert"
    class="center"
  >
    <AppLoader />
  </div>
  <AppNotificationModal
    v-else-if="hasBootstrapFailed"
    :confirm="translations.reloadAction"
    @confirm="reloadPage"
  />
  <router-view v-else />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AppNotificationModal from '@/components/common/AppNotificationModal';
import AppLoader from '@/components/common/loaders/AppLoader';
import integrator from '@/services/integrator';
import eventBus from '@/utility/eventBus';

export default {
  name: 'App',
  components: {
    AppLoader,
    AppNotificationModal,
  },
  data() {
    return {
      isAppLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      token: 'getUserToken',
      isUserLoggedIn: 'isUserLoggedIn',
      isGameModalVisible: 'isGameModalVisible',
      translations: 'translations',
      activeGame: 'getActiveGame',
      hasBootstrapFailed: 'hasBootstrapFailed',
      isIOS12: 'isIOS12',
      isVaixActive: 'isVaixActive',
      isAllGroupSelected: 'isAllGroupSelected',
      isMobileDevice: 'isMobileDevice',
      isLive: 'isLive',
    }),

    isVaixEvent() {
      return this.isUserLoggedIn && this.isVaixActive && this.isAllGroupSelected;
    },

    gameUrl() {
      return this.activeGame?.url;
    },

    gameEventParams() {
      return {
        ...this.activeGame,
        isVaixEvent: this.isVaixEvent,
        isLive: this.isLive,
      };
    },
  },
  watch: {
    isGameModalVisible(isVisible) {
      if (isVisible) {
        integrator.notifyGameEvent('open', this.gameEventParams);
      } else {
        integrator.notifyGameEvent('close', this.activeGame);
      }
    },
    gameUrl() {
      if (!this.gameUrl) return;

      if (this.isGameModalVisible || this.isMobileDevice) {
        integrator.notifyGameEvent('open', this.gameEventParams);
      }
    },
  },
  async created() {
    await this.loadBootstrap();
    this.isAppLoading = false;
  },
  mounted() {
    // on iOS 12, safari mobile 12 the body width changes because of the swipe implementation, so we limit it
    // https://phabricator.nsoft.ba/T119252
    if (this.isIOS12) {
      this.limitBodyWidth();
    }

    integrator.notifyAppLoad();

    eventBus.subscribe('User.AuthorizationChanged', (message) => {
      const wasPreviouslyLoggedIn = this.isUserLoggedIn;

      // changes user logged in state
      this.changeAuthorization(message);

      const didUserLogout = !this.isUserLoggedIn && wasPreviouslyLoggedIn;
      const didUserLogin = this.isUserLoggedIn && !wasPreviouslyLoggedIn;

      if (didUserLogin) {
        this.handleUserLogin();
      }

      if (didUserLogout) {
        this.handleUserLogout();
      }
    });

    eventBus.subscribe('User.BalanceChanged', (message) => {
      this.changeBalance(message.balance);
    });

    eventBus.subscribe('VisualViewportScroll', (message) => {
      this.setVisualViewportScroll(message);
    });

    this.loadCarouselsIfNeeded();

    if (this.isUserLoggedIn) {
      this.loadFavoriteGames(this.token);
    } else {
      this.loadFavoriteGamesForUnauthorized();
    }
  },
  methods: {
    ...mapActions([
      'loadFavoriteGames',
      'loadFavoriteGamesForUnauthorized',
      'changeAuthorization',
      'changeBalance',
      'openNotificationModal',
      'destroyActiveGameUrl',
      'loadBootstrap',
      'loadGames',
      'setVisualViewportScroll',
      'loadCarouselsData',
      'resetCarouselsData',
    ]),

    limitBodyWidth() {
      const viewportWidth = document.querySelector('html').clientWidth;
      document.body.style.maxWidth = `${viewportWidth}px`;
    },

    reloadPage() {
      window.location.reload();
    },

    reloadGamesAndCarouselsIfVaixIsActive() {
      if (!this.isVaixActive) return;

      // clear cache on login/logout
      this.loadGames({ page: 1, shouldClearCache: true });
      this.loadCarouselsIfNeeded();
    },

    loadCarouselsIfNeeded() {
      if (!this.isUserLoggedIn || !this.isVaixActive) return;

      // load carousels if Vaix is active and user is logged in
      this.loadCarouselsData();
    },

    handleUserLogin() {
      this.loadFavoriteGames(this.token);

      if (this.isGameModalVisible) {
        this.onLoginShowNotification();
      }

      this.reloadGamesAndCarouselsIfVaixIsActive();
    },
    handleUserLogout() {
      this.loadFavoriteGamesForUnauthorized();

      if (this.isGameModalVisible) {
        this.onLogoutShowNotification();
        this.destroyActiveGameUrl();
      }

      this.reloadGamesAndCarouselsIfVaixIsActive();
      this.resetCarouselsData();
    },
    onLoginShowNotification() {
      this.openNotificationModal({
        message: this.translations.gameOpenLoginMessage,
      });
    },
    onLogoutShowNotification() {
      const { demoGameOpenLogoutMessage, gameOpenLogoutMessage } = this.translations;
      const message = this.activeGame.demo ? demoGameOpenLogoutMessage : gameOpenLogoutMessage;

      this.openNotificationModal({ message });
    },
  },
};
</script>

<style lang="scss">
.center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
</style>
